import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

const MESES = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

function lastDayOfMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function toISO(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * Seletor compacto de período: navegação de ano + grid de 12 meses.
 * mode="start" grava o 1º dia do mês; mode="end" grava o último dia.
 */
export default function MonthPicker({ label, value, onChange, mode, minYear = 2019, maxYear }) {
  const today = new Date();
  const ceiling = maxYear || today.getFullYear() + 1;
  const selected = value ? new Date(value + "T12:00:00") : null;

  const [viewYear, setViewYear] = useState(
    selected?.getFullYear() ?? today.getFullYear()
  );

  useEffect(() => {
    if (selected) setViewYear(selected.getFullYear());
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const selMonth =
    selected && selected.getFullYear() === viewYear ? selected.getMonth() : -1;

  const pickMonth = (month) => {
    const day = mode === "end" ? lastDayOfMonth(viewYear, month) : 1;
    onChange(toISO(viewYear, month, day));
  };

  return (
    <PickerBox>
      <PickerHead>
        <span className="label">{label}</span>
        <span className={`value ${selected ? "filled" : ""}`}>
          {selected ? `${MESES[selected.getMonth()]}/${selected.getFullYear()}` : "—"}
        </span>
      </PickerHead>

      <YearNav>
        <button
          type="button"
          onClick={() => setViewYear((y) => Math.max(minYear, y - 1))}
          disabled={viewYear <= minYear}
          aria-label="Ano anterior"
        >
          <FiChevronLeft />
        </button>
        <span>{viewYear}</span>
        <button
          type="button"
          onClick={() => setViewYear((y) => Math.min(ceiling, y + 1))}
          disabled={viewYear >= ceiling}
          aria-label="Próximo ano"
        >
          <FiChevronRight />
        </button>
      </YearNav>

      <MonthGrid>
        {MESES.map((m, i) => (
          <MonthCell
            key={m}
            type="button"
            selected={i === selMonth}
            onClick={() => pickMonth(i)}
          >
            {m}
          </MonthCell>
        ))}
      </MonthGrid>
    </PickerBox>
  );
}

/* ---- styled ---- */

const PickerBox = styled.div`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 14px;
  padding: 12px 14px 14px;
  background: ${({ theme }) =>
    theme.body === "#fafbfc" ? "rgba(0,0,0,0.015)" : "rgba(255,255,255,0.02)"};
`;

const PickerHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;

  .label {
    font-size: 0.72em;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.text};
    opacity: 0.55;
  }

  .value {
    font-family: monospace;
    font-size: 0.82em;
    font-weight: 700;
    color: ${({ theme }) => theme.text};
    opacity: 0.4;

    &.filled {
      opacity: 1;
      color: #10b981;
    }
  }
`;

const YearNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;

  span {
    font-family: monospace;
    font-weight: 700;
    font-size: 0.92em;
    color: ${({ theme }) => theme.text};
  }

  button {
    background: ${({ theme }) =>
      theme.body === "#fafbfc" ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.05)"};
    border: none;
    border-radius: 8px;
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 0.85em;
    color: ${({ theme }) => theme.text};

    &:hover:not(:disabled) {
      background: rgba(16, 185, 129, 0.15);
      color: #10b981;
    }
    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }
`;

const MonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 5px;
`;

const MonthCell = styled.button`
  padding: 6px 0;
  border: 1px solid transparent;
  border-radius: 8px;
  font-size: 0.78em;
  font-weight: ${({ selected }) => (selected ? 700 : 500)};
  cursor: pointer;
  background: ${({ selected }) =>
    selected ? "#10b981" : "transparent"};
  color: ${({ selected, theme }) => (selected ? "#fff" : theme.text)};
  opacity: ${({ selected }) => (selected ? 1 : 0.7)};

  &:hover {
    background: ${({ selected }) =>
      selected ? "#10b981" : "rgba(16,185,129,0.15)"};
    color: ${({ selected }) => (selected ? "#fff" : "#10b981")};
    opacity: 1;
  }
`;
